import type { Staff } from '@/api/users';

/**
 * **What a staff account is called on screen, with a floor under it** (BUG-041, 2026-08-06).
 *
 * One rule for every site that names a colleague, because they all read one field and a blank in it
 * was spent six times on §6.10 alone: an empty *Name* cell, a row-menu trigger labelled *"Actions
 * for: "*, two confirmation sentences opening with nothing (*" is in the Recruiter group."*, *" will
 * not be able to sign in…"*), BUG-027's refusal titled *"No new link for "*, and the invitation
 * dialog's *" has an account."* — plus §6.5's interviewer picker, where the option label is built from
 * parts and read *"· Recruiter"*. All measured in a live browser for `rec@acme.test`, an account this
 * product created, whose `username` is blank (BUG-034).
 *
 * ⚠️ **The server now guarantees the field is non-blank, and this is still here.** BUG-034 gave
 * `accounts.services.display_name` a floor of its own — full name → username → email →
 * `UNNAMED_ACCOUNT`, whitespace counted as blank — so on a correct deployment no branch below the
 * first is ever taken. Three things is why it exists anyway, and none of them is defensiveness for its
 * own sake:
 *
 *  * **the contract this client actually holds does not carry the guarantee.** `StaffOut.name` is a
 *    plain `string` in the OpenAPI document, with no `minLength` — unlike `PersonIn.full_name`, which
 *    is where §6.3's candidate half of the same bug got the opposite answer: there the schema forbids
 *    the state, so a fallback would have been a branch nothing can reach and the fix was the comment;
 *  * **the failure it prevents is an unlabelled control**, which §9 forbids outright. The row menu's
 *    entire accessible name is composed from this field, so a blank leaves a button that a screen
 *    reader cannot announce and a voice user cannot ask for;
 *  * **nothing in this repository can fail if the server's floor regresses.** The two repositories
 *    cannot see each other, the guarantee is asserted by a backend test, and §6.10 is a screen only an
 *    Admin ever loads — the shape of defect that sits unseen for a release.
 *
 * ⚠️ **What it falls back to is the row's own email, not a copy of the server's noun phrase.** That is
 * the server's own third step, it is the sentence *Add a colleague* already puts on screen (*"Without
 * a name, their email is what colleagues see."*), and it is what §6.5's picker needs anyway — the
 * reason `StaffOut` carries `email` at all is telling two people with the same name apart. So on the
 * one payload BUG-041 was filed about, the client and the server independently arrive at the same
 * string instead of at two different truths.
 *
 * `lastResort` is passed in rather than read here because these two screens translate in two
 * namespaces (`settings` and `candidates`); `common:unnamedAccount` is the one string, and it is
 * deliberately the same words as the server's `UNNAMED_ACCOUNT` — a noun phrase, because three of the
 * sites above read it **mid-sentence**.
 *
 * Whitespace counts as blank, for the reason the backend's `_first_non_blank` gives: a name of three
 * spaces renders as an empty cell, which is the defect rather than a value that satisfies it.
 */
export function staffName(row: Pick<Staff, 'name' | 'email'>, lastResort: string): string {
  return row.name.trim() || row.email.trim() || lastResort;
}
